// src/features/records/components/LinkedRecord.js
import React from 'react';
import { Link as LinkIcon } from 'lucide-react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import styles from './LinkedRecord.module.css';
import { sanitizeText } from '../../../../utils/sanitize';
import useLinkedRecordPreview from '../../hooks/useLinkedRecordPreview';

const LinkedRecord = ({ link }) => {
  const navigate = useNavigate();
  const { status, title, excerpt } = useLinkedRecordPreview(link);

  const isRecordLink = link.targetType === 'record';
  const isExternal = link.targetType === 'external';
  const isDenied = !isExternal && status === 'denied';
  const recordHref = `/record/${encodeURIComponent(link.targetId)}`;
  const href = isExternal ? link.targetId : recordHref;

  const handleClick = (e) => {
    e.stopPropagation();

    if (
      !isExternal &&
      e.button === 0 &&
      !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
    ) {
      e.preventDefault();

      if (typeof link.targetId === 'string') {
        navigate(recordHref);
      } else {
        console.warn('Unsupported link target type or id:', link);
      }
    }
  };

  const displayTitle = sanitizeText(title);

  // Prepare excerpt text - can be replaced with actual excerpt when available
  const shouldAllowExcerptFallback = !isRecordLink || status === 'ready';
  const excerptText = sanitizeText(
    excerpt ||
    (shouldAllowExcerptFallback ? link.targetExcerpt : '') ||
    (isRecordLink
      ? (status === 'denied'
        ? ''
        : status === 'loading'
          ? 'Fetching link details…'
          : status === 'error'
            ? 'Unable to load link'
            : '')
      : 'Open external link')
  );

  const ariaLabel = isDenied
    ? (displayTitle || 'No permission')
    : (displayTitle ? `Open ${displayTitle}` : 'Open link');
  const wrapperClassName = isDenied
    ? `${styles.linkedRecord} ${styles.linkedRecordDisabled}`
    : styles.linkedRecord;

  if (isDenied) {
    return (
      <div className={wrapperClassName} aria-disabled="true" aria-label={ariaLabel}>
        <div className={styles.linkedTitle}>
          <LinkIcon className={styles.linkIcon} size={16} />
          {displayTitle}
        </div>
        {excerptText && (
          <div className={styles.linkedExcerpt}>
            {excerptText}
          </div>
        )}
      </div>
    );
  }

  return (
    <a
      className={wrapperClassName}
      href={href}
      onClick={handleClick}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      aria-label={ariaLabel}
    >
      <div className={styles.linkedTitle}>
        <LinkIcon className={styles.linkIcon} size={16} />
        {displayTitle}
      </div>
      {excerptText && (
        <div className={styles.linkedExcerpt}>
          {excerptText}
        </div>
      )}
    </a>
  );
};

LinkedRecord.propTypes = {
  link: PropTypes.shape({
    targetId: PropTypes.string.isRequired,
    targetType: PropTypes.oneOf(['record', 'external']).isRequired,
    targetExcerpt: PropTypes.string,
    targetAuthor: PropTypes.shape({
      name: PropTypes.string,
      avatar: PropTypes.string
    })
  }).isRequired
};

export default LinkedRecord;
